<template>
  <div>
    <NavBar>
      <template v-slot:desktop-nav>
        <div class="d-flex" v-if="list">
          <ItemSearch :listId="id" />
          <router-link class="btn-nav btn btn-yellow my-2 my-sm-0 ml-2" :to="'/lists/' + list.id">
            <i class="fas fa-arrow-alt-circle-left mr-1"></i>
            Back
          </router-link>
          <button
            class="btn-nav-green btn btn-green my-2 my-sm-0 ml-2"
            @click="save"
            :disabled="!isDirty || isSaving"
            aria-label="Save rankings"
            title="Save rankings"
          >
            <i class="fas fa-save"></i>
          </button>
        </div>
      </template>
      <template v-slot:mobile-nav>
        <router-link class="mobile-nav-icon btn-nav btn btn-yellow flex-grow-1" :to="'/lists/' + list.id" v-if="list">
          <i class="fas fa-arrow-alt-circle-left"></i>
          Back
        </router-link>
        <button
          class="mobile-nav-icon btn-nav-green btn btn-green list-save-button"
          @click="save"
          :disabled="!isDirty || isSaving"
        >
          <i class="fas fa-save"></i>
          {{ isSaving ? "Saving…" : "Save ranking" }}
        </button>
      </template>
    </NavBar>
    <div class="container home">
      <div class="movie-list-header">
        <div v-cloak v-if="list">
          <h1>{{ list.listTypeId == listType.RANKED ? "Rank" : "Order" }} {{ list.listName }} List</h1>
        </div>
      </div>

      <div v-if="list" class="save-status" role="status" aria-live="polite" :class="{ 'save-error': saveError }">
        {{ saveStatus }}
      </div>

      <div v-if="unRankedItems && unRankedItems.length != 0">
        <h3>You still have titles to rank</h3>
        <p>Rank these to improve the group result.</p>
        <draggable
          v-if="unRankedItems"
          v-model="unRankedItems"
          handle=".sort-icon"
          item-key="id"
          class="list-group"
          id="unranked-group"
          :group="{ name: 'listItems', pull: true, put: false }"
          v-bind="dragOptions"
        >
          <template #item="{element, index}">
            <div class="item-card item-sorting item item-card-action align-items-center flex-column flex-sm-row">
              <div class="d-flex">
                <div class="sort-icon mr-3">
                  <i
                    class="fas fa-sort-up"
                    :class="{ disabled: index == 0 }"
                    @click="moveUnRankedUp(element, index)"
                    :aria-label="'Move ' + element.title + ' up'"
                    :title="'Move ' + element.title + ' up'"
                  ></i>
                  <h2 class="sorting-index-na">N/A</h2>
                  <i
                    class="fas fa-sort-down"
                    @click="moveUnRankedDown(element, index)"
                    :aria-label="'Move ' + element.title + ' down'"
                    :title="'Move ' + element.title + ' down'"
                  ></i>
                </div>
                <img class="float-left" v-bind:src="element.poster" alt="" />
              </div>
              <div class="movie-item-body">
                <div class="flex">
                  <h2 class="mb-1">
                    {{ element.title }}
                    <span class="year">({{ element.year }})</span>
                  </h2>
                </div>
                <p class="mb-1">{{ element.plot }}</p>
                <small v-for="rating in element.itemRatings" :key="rating">
                  <b>{{ rating.ratingSource.ratingName }}</b>
                  :
                  <span>{{ rating.ratingValue }}</span>
                  •
                </small>
              </div>
              <small class="date">{{ element.addedWhen }}</small>
            </div>
          </template>
        </draggable>
        <hr />
      </div>

      <h3>Ranked</h3>
      <draggable
        v-model="rankedItems"
        handle=".sort-icon"
        item-key="id"
        v-bind="dragOptions"
        :group="{ name: 'listItems', pull: false, put: true }"
        :move="itemMoved"
      >
        <template #header>
          <div class="item-sorting item-template" v-if="unRankedItems && unRankedItems.length != 0">
            <h2 class="">Drag items here to rank</h2>
          </div>

          <EmptyState
            v-if="rankedItems && rankedItems.length === 0 && unRankedItems && unRankedItems.length === 0"
            title="No titles yet"
            description="Add a few movies or TV series so your group can start ranking.">
            <template #action>
              <ItemSearch :listId="id" />
            </template>
          </EmptyState>
        </template>
        <template #item="{element, index}">
          <div>
            <div
              class="item-card item-sorting ranked-item item-card-action align-items-center flex-column flex-sm-row"
              :data-testid="'ranked-item-' + element.id">
              <div class="ranked-item-summary d-flex align-items-center">
                <div
                  class="sort-icon rank-controls mr-3"
                  :aria-label="'Drag ' + element.title + ' to reorder'"
                  :title="'Drag ' + element.title + ' to reorder'">
                  <button
                    type="button"
                    class="rank-move-button"
                    :disabled="index == 0"
                    @click="moveUp(element)"
                    :aria-label="'Move ' + element.title + ' up'"
                    :title="'Move ' + element.title + ' up'">
                    <i class="fas fa-sort-up" aria-hidden="true"></i>
                  </button>
                  <h2 :aria-label="'Position ' + getScore(element)">{{ getScore(element) }}</h2>
                  <button
                    type="button"
                    class="rank-move-button"
                    :disabled="index == rankedItems.length - 1"
                    @click="moveDown(element)"
                    :aria-label="'Move ' + element.title + ' down'"
                    :title="'Move ' + element.title + ' down'">
                    <i class="fas fa-sort-down" aria-hidden="true"></i>
                  </button>
                </div>
                <img class="float-left" v-bind:src="element.poster" alt="" />
              </div>
              <div class="movie-item-body">
                <div class="flex">
                  <h2 class="mb-1">
                    {{ element.title }}
                    <span class="year">({{ element.year }})</span>
                  </h2>
                </div>
                <p class="mb-1">{{ element.plot }}</p>
                <small v-for="rating in element.itemRatings" :key="rating">
                  <b>{{ rating.ratingSource.ratingName }}</b>
                  :
                  <span>{{ rating.ratingValue }}</span>
                  •
                </small>
                <label class="move-to-position">
                  <span>Position</span>
                  <select
                    :value="index + 1"
                    @change="moveToPosition(element, $event.target.value)"
                    :aria-label="'Move ' + element.title + ' to position'"
                    :data-testid="'move-to-position-' + element.id">
                    <option v-for="position in rankedItems.length" :key="position" :value="position">
                      {{ position }}
                    </option>
                  </select>
                </label>
              </div>
              <!-- <small class="date">{{ element.addedWhen }}</small> -->
            </div>
          </div>
        </template>
      </draggable>
    </div>
  </div>
</template>

<script>
import NavBar from "@/components/NavBar.vue";
import ListService from "../services/ListService";
import draggable from "vuedraggable";
import ItemSearch from "@/components/ItemSearch.vue";
import ListType from "../utilities/listTypes";
import EmptyState from "@/components/EmptyState.vue";

export default {
  name: "OrderList",
  props: ["id"],
  data() {
    return {
      list: null,
      unRankedItems: null,
      rankedItems: null,
      listType: ListType,
      savedOrderIds: [],
      isSaving: false,
      saveError: null,
      hasSaved: false
    };
  },
  components: {
    NavBar,
    draggable,
    ItemSearch,
    EmptyState
  },
  computed: {
    currentOrderIds() {
      return this.getOrderIds(this.rankedItems);
    },
    isDirty() {
      return JSON.stringify(this.currentOrderIds) !== JSON.stringify(this.savedOrderIds);
    },
    saveStatus() {
      if (this.isSaving) {
        return "Saving…";
      }

      if (this.saveError) {
        return this.saveError;
      }

      if (this.isDirty) {
        return "Unsaved changes";
      }

      return this.hasSaved ? "Saved" : "All changes saved";
    },
    dragOptions() {
      return {
        animation: 200,
        group: "description",
        disabled: false,
        ghostClass: "ghost",
        dragClass: "dragging"
      };
    }
  },
  methods: {
    getOrderIds(items) {
      return (items || []).map(item => item.id);
    },
    moveUnRankedUp(item) {
      let index = this.unRankedItems.indexOf(item);

      if (index != 0) {
        var itemObj = this.unRankedItems.splice(index, 1)[0];

        this.unRankedItems.splice(index - 1, 0, itemObj);
      }
    },
    moveUnRankedDown(item) {
      let index = this.unRankedItems.indexOf(item);

      var itemObj = this.unRankedItems.splice(index, 1)[0];

      if (index < this.unRankedItems.length) {
        this.unRankedItems.splice(index + 1, 0, itemObj);
      } else {
        // Move to ranked list
        this.rankedItems.splice(0, 0, itemObj);
      }
    },
    moveUp(item) {
      let index = this.rankedItems.indexOf(item);

      if (index != 0) {
        var itemObj = this.rankedItems.splice(index, 1)[0];

        this.rankedItems.splice(index - 1, 0, itemObj);
      }
    },
    moveDown(item) {
      let index = this.rankedItems.indexOf(item);

      if (index < this.rankedItems.length - 1) {
        var itemObj = this.rankedItems.splice(index, 1)[0];

        this.rankedItems.splice(index + 1, 0, itemObj);
      }
    },
    moveToPosition(item, targetPosition) {
      const currentIndex = this.rankedItems.indexOf(item);
      const position = Number(targetPosition);

      if (currentIndex === -1 || !Number.isInteger(position) || position < 1 || position > this.rankedItems.length) {
        return;
      }

      const targetIndex = position - 1;

      if (currentIndex === targetIndex) {
        return;
      }

      const itemObj = this.rankedItems.splice(currentIndex, 1)[0];
      this.rankedItems.splice(targetIndex, 0, itemObj);
    },
    save() {
      if (!this.isDirty || this.isSaving) {
        return;
      }

      const submittedItems = [...this.rankedItems];
      const submittedOrderIds = this.getOrderIds(submittedItems);

      this.isSaving = true;
      this.saveError = null;

      ListService.saveRankedItems(this.list.id, submittedItems)
        .then(result => {
          if (result == "success") {
            this.savedOrderIds = submittedOrderIds;
            this.hasSaved = true;
            this.$toast.success("Successfully Saved Rankings");
          } else {
            this.saveError = "Unable to save ranking. Your changes are still unsaved.";
            this.$toast.error("Unable To Save Ranked List");
          }
        })
        .catch(error => {
          this.saveError = "Unable to save ranking. Your changes are still unsaved.";
          return error.handleError();
        })
        .finally(() => {
          this.isSaving = false;
        });
    },
    itemMoved(evt) {
      var originalScore = evt.relatedContext.element.score;

      evt.relatedContext.element.score = evt.draggedContext.element.score;

      evt.draggedContext.element.score = originalScore;
    },
    getScore(element) {
      return this.rankedItems.indexOf(element) + 1;
    },
    warnBeforeUnload(event) {
      if (!this.isDirty) {
        return;
      }

      event.preventDefault();
      event.returnValue = "";
    }
  },
  beforeRouteEnter(_to, _from, next) {
    Promise.all([ListService.getList(_to.params.id), ListService.getRankedList(_to.params.id)])
      .then(results => {
        next(vm => {
          vm.list = results[0].list;
          vm.rankedItems = results[1].rankedItems;
          vm.unRankedItems = results[1].unRankedItems;
          vm.savedOrderIds = vm.getOrderIds(results[1].rankedItems);
        });
      })
      .catch(error => {
        const status = error.response ? error.response.status : null;

        if (status === 403) {
          return next({ name: "Forbidden", replace: true });
        }

        if (status === 404) {
          return next({ name: "Missing Resource", replace: true });
        }

        return error.handleError();
      });
  },
  beforeRouteLeave(_to, _from, next) {
    if (this.isDirty && !window.confirm("You have unsaved ranking changes. Leave without saving?")) {
      return next(false);
    }

    return next();
  },
  mounted() {
    window.addEventListener("beforeunload", this.warnBeforeUnload);
  },
  beforeUnmount() {
    window.removeEventListener("beforeunload", this.warnBeforeUnload);
  }
};
</script>

<style scoped>
.sortable-chosen.item-card-action {
  background-color: rgb(121, 131, 182);
  border: solid #e8c547 5px !important;
  z-index: inherit;
}

.save-status {
  margin-bottom: 1rem;
  font-weight: 600;
}

.save-error {
  color: #dc3545;
}

button:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.sortable-chosen {
  opacity: 1;
}

.ghost.item-card-action {
  background-color: rgb(121, 131, 182);
  border: solid #e8c547 5px !important;
  z-index: inherit;
}

.item-card:before {
  background: none;
}

.movie-item-body {
  margin-left: 20px;
  width: 100%;
}

.movie-ratings small b {
  color: #e8c547 !important;
}

.item-sorting .fa,
.item-sorting .sort-icon {
  font-size: 50px;
  align-self: center;
}

.item-sorting .sort-icon .disabled {
  color: #2125284a;
}

.rank-move-button {
  align-items: center;
  background: transparent;
  border: 0;
  color: inherit;
  display: flex;
  height: 24px;
  justify-content: center;
  padding: 0;
  width: 44px;
}

.rank-move-button:focus-visible,
.move-to-position select:focus-visible {
  outline: 3px solid #e8c547;
  outline-offset: 2px;
}

.rank-move-button:disabled {
  color: #2125284a;
}

.item-sorting .sort-icon {
  display: flex;
  flex-direction: column;
  text-align: center;
  height: 79px;
}

.item-sorting .sort-icon:hover {
  color: #e8c547 !important;
  cursor: grab;
}

.item-sorting .sort-icon > .fas {
  height: 20px;
}

.item-sorting .sort-icon > .rank-move-button .fas {
  font-size: 42px;
  line-height: 0.5;
}

.item-sorting .sort-icon > h2 {
  margin: 0;
  line-height: 1;
  margin-bottom: 5px;
  font-family: "Roboto", sans-serif;
  height: 28px;
  margin-top: 5px;
}

.item-sorting .fa-sort-down {
  line-height: 0;
}

.list-save-button {
  flex-grow: 4;
}

.move-to-position {
  align-items: center;
  display: flex;
  font-weight: 600;
  gap: 0.5rem;
  margin: 0.75rem 0 0;
  width: fit-content;
}

.move-to-position select {
  border: 2px solid #393d55;
  border-radius: 4px;
  min-height: 44px;
  min-width: 64px;
  padding: 0.25rem 0.5rem;
}

.item-sorting img {
  width: 90px;
  height: 157px;
}

.item-sorting {
}

.item-sorting .fa-sort:hover {
  cursor: grab;
}

.item-sorting:hover {
  border: solid #393d55 5px !important;
}

.fa-sort:hover {
  color: #e8c547 !important;
}

.draggable-mirror {
  outline: -webkit-focus-ring-color auto 1px;
}

.sorting-index,
.sorting-index-na {
  align-self: center;
  margin-right: 20px;
  width: 20px;
  line-height: 2 !important;
  transform: translateX(-20%);
  font-size: 1rem;
}

#unranked-group .item-sorting {
  background-color: #898989;
}

.item-template {
  height: 157px;
  border: 5px #80808080 dashed;
  color: #80808080;
  background-color: #8080800d;
  display: flex;
}

.item-template h2 {
  vertical-align: middle;
  margin: auto;
}

@media (max-width: 991.98px) {
  .item-sorting .sort-icon > h2 {
    line-height: 1.4;
  }
}

/* Extra small devices (portrait phones, less than 576px) */
@media (max-width: 575.98px) {
  #unranked-group .sort-icon {
    left: 40px;
    position: absolute;
  }

  .ranked-item {
    align-items: stretch !important;
    flex-direction: row !important;
    min-height: 0;
    padding: 0.75rem;
  }

  .ranked-item-summary {
    flex: 0 0 auto;
  }

  .ranked-item .sort-icon {
    height: 124px;
    margin-right: 0.75rem !important;
    position: static;
  }

  .ranked-item .rank-move-button {
    flex: 0 0 44px;
    height: 44px;
    width: 48px;
  }

  .ranked-item img {
    display: none;
  }

  .ranked-item .movie-item-body {
    margin-left: 0;
    min-width: 0;
  }

  .ranked-item .movie-item-body h2 {
    font-size: 1.25rem;
  }

  .ranked-item .movie-item-body > p,
  .ranked-item .movie-item-body > small {
    display: none;
  }

  .move-to-position {
    margin-top: 0.5rem;
  }
}
</style>
